import React, { useState, useEffect, useRef, useCallback } from "react";
import { useApp, apiClient, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Lock, Settings, Users, Calendar, Trophy, Plus, 
  Trash2, Edit, ArrowLeft, X, Save, FileText, 
  AlertTriangle, Shield, ShieldCheck, Clock, Upload, Image, RotateCcw
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";

// Alphanumeric PIN Pad Component
const PinInput = ({ value, onChange, onSubmit, loading }) => {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    onChange(raw);
    if (raw.length === 7 && /^[A-Z]{3}[0-9]{4}$/.test(raw)) {
      onSubmit(raw);
    }
  };

  const isValid = value.length === 0 || /^[A-Z]{0,3}[0-9]{0,4}$/.test(value);

  return (
    <div className="space-y-4" data-testid="pin-input">
      <div className="flex justify-center gap-1 mb-2">
        {[0, 1, 2].map((i) => (
          <div key={`l-${i}`} className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-all ${value[i] ? "border-primary bg-primary/10 text-primary" : "border-border bg-white"}`}>
            {value[i] || ""}
          </div>
        ))}
        <div className="w-2" />
        {[3, 4, 5, 6].map((i) => (
          <div key={`n-${i}`} className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-all ${value[i] ? "border-primary bg-primary/10 text-primary" : "border-border bg-white"}`}>
            {value[i] || ""}
          </div>
        ))}
      </div>
      <p className="text-xs text-center text-muted-foreground">Format: 3 letters + 4 numbers (e.g., ABC1234)</p>
      <input
        ref={inputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        maxLength={7}
        value={value}
        onChange={handleChange}
        disabled={loading}
        placeholder="Enter PIN"
        className={`w-full text-center text-lg font-mono tracking-widest py-3 rounded-lg border-2 outline-none transition-all ${!isValid ? 'border-red-400 bg-red-50' : 'border-border focus:border-primary'}`}
        data-testid="pin-text-input"
      />
      {loading && <div className="flex justify-center"><div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" /></div>}
    </div>
  );
};

// Club Item with Logo Upload Component
const ClubItemWithLogo = ({ club, adminPin, onEdit, onDelete, onLogoUploaded, imgCacheKey }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPG, PNG, GIF, WebP images allowed");
      return;
    }
    
    // Validate file size (1MB max)
    if (file.size > 1024 * 1024) {
      toast.error("Logo must be under 1MB");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await apiClient.post(`/upload/club-logo/${club.id}?admin_pin=${adminPin}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Logo uploaded");
      onLogoUploaded();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };
  
  const logoUrl = club.logo_path 
    ? `${API}/images/${club.logo_path.replace('ketley-league/', '')}?v=${imgCacheKey}`
    : null;
  
  return (
    <div className="bg-white rounded-lg border border-border p-3" data-testid={`club-item-${club.id}`}>
      <div className="flex items-center gap-3">
        {/* Logo */}
        <div className="relative">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleLogoUpload}
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden hover:opacity-80 transition-opacity border-2 border-dashed border-border"
            title="Click to upload logo"
          >
            {uploading ? (
              <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
            ) : logoUrl ? (
              <img src={logoUrl} alt={club.name} className="w-full h-full object-cover" />
            ) : (
              <Upload size={18} className="text-muted-foreground" />
            )}
          </button>
        </div>
        
        {/* Club Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium">{club.name}</p>
          <p className="text-xs text-muted-foreground">PIN: {club.pin}</p>
          {(club.captain_name || club.admin_name) && (
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
              {club.captain_name && (
                <p className="text-xs text-muted-foreground truncate">
                  <span className="font-medium">Capt:</span> {club.captain_name}
                </p>
              )}
              {club.admin_name && (
                <p className="text-xs text-muted-foreground truncate">
                  <span className="font-medium">Admin:</span> {club.admin_name}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" onClick={onEdit}><Edit size={16} /></Button>
          <Button size="icon" variant="ghost" onClick={onDelete}><Trash2 size={16} className="text-destructive" /></Button>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = ({ adminPin, adminType, onLogout }) => {
  const { clubs, seasons, fixtures, results, pendingResults, adminSettings, refreshData, selectedSeason } = useApp();
  const isSuperAdmin = adminType === "super_admin";
  const [sessionTimeLeft, setSessionTimeLeft] = useState("");
  
  
  const [activeTab, setActiveTab] = useState("clubs");
  const [showClubDialog, setShowClubDialog] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [clubForm, setClubForm] = useState({ name: "", pin: "", slope_rating: "113", course_rating: "72", par: "72", captain_name: "", captain_email: "", captain_phone: "", admin_name: "", admin_email: "", admin_phone: "" });
  const [showSeasonDialog, setShowSeasonDialog] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [seasonForm, setSeasonForm] = useState({ name: "", start_date: "", end_date: "", is_active: false });
  const [fixtureEdits, setFixtureEdits] = useState({});
  const [savingFixtures, setSavingFixtures] = useState(false);
  const [fixtureClubFilter, setFixtureClubFilter] = useState('all');
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [resultForm, setResultForm] = useState({ home_score: "", away_score: "" });
  const [conflictEntries, setConflictEntries] = useState([]);
  const [editingPending, setEditingPending] = useState(null);
  const [pendingForm, setPendingForm] = useState({ home_score: "", away_score: "" });
  const [settingsForm, setSettingsForm] = useState({ league_name: "", super_admin_pin: "", standard_admin_pin: "", rules: "", contacts_info: "" });
  const [clubsAdmin, setClubsAdmin] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const headerImageRef = useRef(null);
  const bgImageRef = useRef(null);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [imgCacheKey, setImgCacheKey] = useState(Date.now());

  useEffect(() => {
    fetchClubsAdmin();
    fetchFullSettings();
    if (isSuperAdmin) {
      fetchAdminLogs();
    }
  }, []);

  useEffect(() => {
    if (adminSettings) {
      setSettingsForm(prev => ({
        ...prev,
        league_name: adminSettings.league_name || "",
        rules: adminSettings.rules || "",
        contacts_info: adminSettings.contacts_info || ""
      }));
    }
  }, [adminSettings]);

  // Session countdown timer
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;
  const warnedRef = useRef(false);
  
  const extendSession = useCallback(() => {
    const session = getStoredSession();
    if (session) {
      session.timestamp = Date.now();
      sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
      warnedRef.current = false;
      toast.success("Session extended by 30 minutes");
    }
  }, []);

  useEffect(() => {
    const tick = () => {
      try {
        const session = getStoredSession();
        if (!session) {
          onLogoutRef.current();
          return;
        }
        const remaining = SESSION_DURATION_MS - (Date.now() - session.timestamp);
        if (remaining <= 0) {
          toast.error("Session expired");
          onLogoutRef.current();
          return;
        }
        // Warn at 2 minutes
        if (remaining <= 120000 && !warnedRef.current) {
          warnedRef.current = true;
          toast.warning("Session expires in 2 minutes", {
            duration: 10000,
            action: {
              label: "Extend 30 min",
              onClick: () => extendSession(),
            },
          });
        }
        const mins = Math.floor(remaining / 60000);
        const secs = Math.floor((remaining % 60000) / 1000);
        setSessionTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
      } catch (e) {
        console.error("Session timer error:", e);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [extendSession]);

  const fetchClubsAdmin = async () => {
    try {
      const response = await apiClient.get(`/clubs/admin?admin_pin=${adminPin}`);
      setClubsAdmin(response.data);
    } catch (error) {
      toast.error("Failed to fetch clubs");
    }
  };

  const fetchAdminLogs = async () => {
    try {
      const response = await apiClient.get(`/admin/logs?admin_pin=${adminPin}&limit=100`);
      setAdminLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch logs");
    }
  };

  const fetchFullSettings = async () => {
    try {
      const response = await apiClient.get(`/admin/settings/full?admin_pin=${adminPin}`);
      setSettingsForm({
        league_name: response.data.league_name || "",
        super_admin_pin: response.data.super_admin_pin || "",
        standard_admin_pin: response.data.standard_admin_pin || "",
        rules: response.data.rules || "",
        contacts_info: response.data.contacts_info || ""
      });
    } catch (error) {
      console.error("Failed to fetch full settings");
    }
  };

  // Club CRUD
  const handleSaveClub = async () => {
    if (!clubForm.name || !clubForm.pin) {
      toast.error("Name and PIN are required");
      return;
    }
    if (!/^[A-Za-z]{3}[0-9]{4}$/.test(clubForm.pin)) {
      toast.error("PIN must be 3 letters + 4 numbers (e.g., ABC1234)");
      return;
    }
    
    setLoading(true);
    const payload = {
      ...clubForm,
      slope_rating: parseFloat(clubForm.slope_rating) || 113,
      course_rating: parseFloat(clubForm.course_rating) || 72,
      par: parseInt(clubForm.par) || 72
    };
    try {
      if (editingClub) {
        await apiClient.put(`/clubs/${editingClub.id}?admin_pin=${adminPin}`, payload);
        toast.success("Club updated");
      } else {
        await apiClient.post(`/clubs?admin_pin=${adminPin}`, payload);
        toast.success("Club created");
      }
      setShowClubDialog(false);
      setEditingClub(null);
      setClubForm({ name: "", pin: "", slope_rating: "113", course_rating: "72", par: "72", captain_name: "", captain_email: "", captain_phone: "", admin_name: "", admin_email: "", admin_phone: "" });
      fetchClubsAdmin();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save club");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async (clubId) => {
    if (!window.confirm("Delete this club?")) return;
    try {
      await apiClient.delete(`/clubs/${clubId}?admin_pin=${adminPin}`);
      toast.success("Club deleted");
      fetchClubsAdmin();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete club");
    }
  };

  const openEditClub = (club) => {
    setEditingClub(club);
    setClubForm({
      name: club.name,
      pin: club.pin.includes("***") ? "" : club.pin,
      slope_rating: String(club.slope_rating ?? 113),
      course_rating: String(club.course_rating ?? 72),
      par: String(club.par ?? 72),
      captain_name: club.captain_name || "",
      captain_email: club.captain_email || "",
      captain_phone: club.captain_phone || "",
      admin_name: club.admin_name || "",
      admin_email: club.admin_email || "",
      admin_phone: club.admin_phone || ""
    });
    setShowClubDialog(true);
  };

  // Season CRUD
  const handleSaveSeason = async () => {
    setLoading(true);
    try {
      if (editingSeason) {
        await apiClient.put(`/seasons/${editingSeason.id}?admin_pin=${adminPin}`, seasonForm);
        toast.success("Season updated");
      } else {
        await apiClient.post(`/seasons?admin_pin=${adminPin}`, seasonForm);
        toast.success("Season created");
      }
      setShowSeasonDialog(false);
      setEditingSeason(null);
      setSeasonForm({ name: "", start_date: "", end_date: "", is_active: false });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save season");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSeason = async (seasonId) => {
    if (!window.confirm("Delete this season and all fixtures/results?")) return;
    try {
      await apiClient.delete(`/seasons/${seasonId}?admin_pin=${adminPin}`);
      toast.success("Season deleted");
      refreshData();
    } catch (error) {
      toast.error("Failed to delete season");
    }
  };

  const openEditSeason = (season) => {
    setEditingSeason(season);
    setSeasonForm({ name: season.name, start_date: season.start_date, end_date: season.end_date, is_active: season.is_active });
    setShowSeasonDialog(true);
  };

  const handleGenerateFixtures = async () => {
    if (!selectedSeason) {
      toast.error("Please select a season first");
      return;
    }
    if (!window.confirm("This will delete all existing fixtures and results for this season. Continue?")) return;
    try {
      const response = await apiClient.post(`/fixtures/generate?season_id=${selectedSeason.id}&admin_pin=${adminPin}`);
      toast.success(`Generated ${response.data.created} fixtures`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate fixtures");
    }
  };

  // Reset fixture
  const handleResetFixture = async (fixture) => {
    if (!window.confirm(`Reset "${fixture.home_club_name} vs ${fixture.away_club_name}"? This will clear ALL results and pending entries, and unlock the date for rescheduling.`)) return;
    try {
      await apiClient.post(`/fixtures/${fixture.id}/reset?admin_pin=${adminPin}`);
      toast.success("Fixture reset successfully");
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset fixture");
    }
  };

  // Fixture date bulk update
  const handleFixtureDateChange = (fixtureId, date) => {
    setFixtureEdits(prev => ({ ...prev, [fixtureId]: date }));
  };

  const handleSaveFixtureDates = async () => {
    const updates = Object.entries(fixtureEdits).map(([fixture_id, match_date]) => ({ fixture_id, match_date }));
    if (updates.length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSavingFixtures(true);
    try {
      const response = await apiClient.put(`/fixtures/bulk-dates?admin_pin=${adminPin}`, { fixture_updates: updates });
      toast.success(`Updated ${response.data.updated} fixture dates`);
      setFixtureEdits({});
      refreshData();
    } catch (error) {
      toast.error("Failed to save fixture dates");
    } finally {
      setSavingFixtures(false);
    }
  };

  // Result editing
  const openEditResult = async (result) => {
    setEditingResult(result);
    setResultForm({ home_score: result.home_score.toString(), away_score: result.away_score.toString() });
    setConflictEntries([]);
    setShowResultDialog(true);

    if (result.status === 'conflict') {
      try {
        const res = await apiClient.get(`/results/${result.id}/conflict-entries?admin_pin=${adminPin}`);
        setConflictEntries(res.data);
      } catch (err) {
        console.error("Failed to fetch conflict entries", err);
      }
    }
  };

  const handleSaveResult = async () => {
    setLoading(true);
    try {
      const endpoint = editingResult.status === 'conflict' 
        ? `/results/${editingResult.id}/resolve-conflict?admin_pin=${adminPin}`
        : `/results/${editingResult.id}?admin_pin=${adminPin}`;
      
      const method = editingResult.status === 'conflict' ? 'post' : 'put';
      await apiClient[method](endpoint, {
        home_score: parseFloat(resultForm.home_score),
        away_score: parseFloat(resultForm.away_score)
      });
      toast.success(editingResult.status === 'conflict' ? "Conflict resolved" : "Result updated");
      setShowResultDialog(false);
      setEditingResult(null);
      refreshData();
      if (isSuperAdmin) fetchAdminLogs();
    } catch (error) {
      toast.error("Failed to update result");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResult = async (resultId) => {
    if (!window.confirm("Delete this result?")) return;
    try {
      await apiClient.delete(`/results/${resultId}?admin_pin=${adminPin}`);
      toast.success("Result deleted");
      refreshData();
      if (isSuperAdmin) fetchAdminLogs();
    } catch (error) {
      toast.error("Failed to delete result");
    }
  };

  // Edit pending entry
  const handleEditPending = (pending) => {
    setEditingPending(pending);
    setPendingForm({ home_score: String(pending.home_score), away_score: String(pending.away_score) });
  };

  const handleSavePending = async () => {
    if (!editingPending) return;
    setLoading(true);
    try {
      await apiClient.put(`/pending-results/${editingPending.id}?admin_pin=${adminPin}`, {
        home_score: parseFloat(pendingForm.home_score),
        away_score: parseFloat(pendingForm.away_score)
      });
      toast.success("Pending entry updated");
      setEditingPending(null);
      refreshData();
      if (isSuperAdmin) fetchAdminLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pending entry");
    } finally {
      setLoading(false);
    }
  };

  // Settings
  const handleSaveSettings = async () => {
    // Validate PIN formats
    if (settingsForm.super_admin_pin && !/^[A-Za-z]{3}[0-9]{4}$/.test(settingsForm.super_admin_pin)) {
      toast.error("Super Admin PIN must be 3 letters + 4 numbers");
      return;
    }
    if (settingsForm.standard_admin_pin && !/^[A-Za-z]{3}[0-9]{4}$/.test(settingsForm.standard_admin_pin)) {
      toast.error("Standard Admin PIN must be 3 letters + 4 numbers");
      return;
    }
    
    // Standard admin only sends fields they can change
    const payload = isSuperAdmin ? settingsForm : {
      standard_admin_pin: settingsForm.standard_admin_pin,
      contacts_info: settingsForm.contacts_info,
    };
    
    setLoading(true);
    try {
      await apiClient.put(`/admin/settings?admin_pin=${adminPin}`, payload);
      toast.success("Settings saved");
      refreshData();
      if (isSuperAdmin) fetchAdminLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleInitData = async () => {
    if (!window.confirm("This will create 6 default clubs and a 2025 season. Continue?")) return;
    try {
      await apiClient.post(`/init-data?admin_pin=${adminPin}`);
      toast.success("Default data initialized");
      fetchClubsAdmin();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initialize data");
    }
  };

  // Header Image Upload
  const handleHeaderImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPG, PNG, GIF, WebP images allowed");
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      return;
    }
    
    setUploadingHeader(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await apiClient.post(`/upload/header-image?admin_pin=${adminPin}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Header image uploaded");
      setImgCacheKey(Date.now());
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingHeader(false);
    }
  };

  // Background Image Upload
  const handleBgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) { toast.error("Only JPG, PNG, GIF, WebP images allowed"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
    
    setUploadingBg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post(`/upload/background-image?admin_pin=${adminPin}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Background image uploaded");
      setImgCacheKey(Date.now());
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally {
      setUploadingBg(false);
      if (bgImageRef.current) bgImageRef.current.value = '';
    }
  };

  const handleDeleteBgImage = async (path) => {
    try {
      await apiClient.delete(`/upload/background-image?path=${encodeURIComponent(path)}&admin_pin=${adminPin}`);
      toast.success("Background removed");
      refreshData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || "Failed to delete background";
      toast.error(detail);
    }
  };

  const seasonFixtures = fixtures.filter(f => f.season_id === selectedSeason?.id);
  const seasonResults = results.filter(r => r.season_id === selectedSeason?.id);
  const conflictResults = seasonResults.filter(r => r.status === 'conflict');

  const formatLogTime = (timestamp) => {
    try {
      return format(parseISO(timestamp), "MMM d, HH:mm");
    } catch {
      return timestamp;
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ backgroundColor: '#ffffff', position: 'relative', zIndex: 10 }}>
      <div className="bg-primary px-4 pt-8 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-extrabold text-white tracking-tight">Admin Panel</h1>
              {isSuperAdmin ? (
                <span className="text-xs bg-[#CCFF00] text-primary px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <ShieldCheck size={12} /> Super
                </span>
              ) : (
                <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <Shield size={12} /> Standard
                </span>
              )}
            </div>
            <p className="text-white/70 text-sm mt-1">
              {isSuperAdmin ? "Full access to all features" : "Clubs, fixtures, results & contacts"}
            </p>
          </div>
          <Button variant="ghost" onClick={onLogout} className="text-white hover:bg-white/20" data-testid="admin-logout">
            <X size={20} />
          </Button>
        </div>
        {/* Session timer */}
        <div className="flex items-center gap-2 mt-2 text-white/60 text-xs">
          <Clock size={12} />
          <span data-testid="session-timer">Session expires in {sessionTimeLeft}</span>
        </div>
      </div>

      <div className="px-4 py-4 pb-24">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`w-full grid mb-4 h-12 ${isSuperAdmin ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <TabsTrigger value="clubs" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">
              <Users size={14} className="mr-1" /> Clubs
            </TabsTrigger>
            <TabsTrigger value="seasons" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">
              <Calendar size={14} className="mr-1" /> Seasons
            </TabsTrigger>
            <TabsTrigger value="fixtures" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">
              <Trophy size={14} className="mr-1" /> Fixtures
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">
              <Settings size={14} className="mr-1" /> Settings
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="logs" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">
                <FileText size={14} className="mr-1" /> Logs
              </TabsTrigger>
            )}
          </TabsList>

          {/* Clubs Tab - Both Admin Types */}
          <TabsContent value="clubs" className="space-y-4" data-testid="clubs-tab-content">
              <div className="flex justify-between items-center">
                <h2 className="font-semibold">Clubs ({clubsAdmin.length})</h2>
                <Button size="sm" onClick={() => { setEditingClub(null); setClubForm({ name: "", pin: "", captain_name: "", captain_email: "", captain_phone: "", admin_name: "", admin_email: "", admin_phone: "" }); setShowClubDialog(true); }} data-testid="add-club-btn">
                  <Plus size={16} className="mr-1" /> Add Club
                </Button>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Logo size:</strong> 200x200px recommended (square). Max 1MB.
                </p>
              </div>
              
              {clubsAdmin.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">No clubs yet</p>
                  <Button onClick={handleInitData} variant="outline" data-testid="init-data-btn">Initialize Default Data</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {clubsAdmin.map((club) => (
                    <ClubItemWithLogo 
                      key={club.id} 
                      club={club} 
                      adminPin={adminPin}
                      imgCacheKey={imgCacheKey}
                      onEdit={() => openEditClub(club)}
                      onDelete={() => handleDeleteClub(club.id)}
                      onLogoUploaded={() => { setImgCacheKey(Date.now()); fetchClubsAdmin(); refreshData(); }}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

          {/* Seasons Tab */}
          <TabsContent value="seasons" className="space-y-4" data-testid="seasons-tab-content">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">Seasons ({seasons.length})</h2>
              <Button size="sm" onClick={() => { setEditingSeason(null); setSeasonForm({ name: "", start_date: "", end_date: "", is_active: false }); setShowSeasonDialog(true); }} data-testid="add-season-btn">
                <Plus size={16} className="mr-1" /> Add Season
              </Button>
            </div>
            {seasons.length > 0 && (
              <Button variant="outline" className="w-full" onClick={handleGenerateFixtures} data-testid="generate-fixtures-btn">
                <Trophy size={16} className="mr-2" /> Generate Fixtures for Selected Season
              </Button>
            )}
            <div className="space-y-2">
              {seasons.map((season) => (
                <div key={season.id} className="bg-white rounded-lg border border-border p-3 flex items-center justify-between" data-testid={`season-item-${season.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{season.name}</p>
                      {season.is_active && <span className="text-xs bg-[#CCFF00] text-primary px-2 py-0.5 rounded-full font-medium">Active</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{season.start_date} - {season.end_date}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => openEditSeason(season)}><Edit size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDeleteSeason(season.id)}><Trash2 size={16} className="text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Fixtures Tab */}
          <TabsContent value="fixtures" className="space-y-4" data-testid="fixtures-tab-content">
            {/* Conflicts */}
            {conflictResults.length > 0 && (
              <div className="mb-6">
                <h2 className="font-semibold text-red-600 flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} /> Conflicts to Resolve ({conflictResults.length})
                </h2>
                <div className="space-y-2">
                  {conflictResults.map((result) => (
                    <div key={result.id} className="bg-red-50 rounded-lg border border-red-200 p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{result.home_club_name} vs {result.away_club_name}</p>
                        <p className="text-xs text-red-600">Teams entered different scores</p>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => openEditResult(result)}>Resolve</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {seasonResults.filter(r => r.status === 'confirmed').length > 0 && (
              <div className="mb-6">
                <h2 className="font-semibold mb-3">Confirmed Results ({seasonResults.filter(r => r.status === 'confirmed').length})</h2>
                <div className="space-y-2">
                  {seasonResults.filter(r => r.status === 'confirmed').map((result) => (
                    <div key={result.id} className="bg-white rounded-lg border border-border p-3 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{result.home_club_name} {result.home_score} - {result.away_score} {result.away_club_name}</p>
                        <p className="text-xs text-muted-foreground">Points: {result.home_points} - {result.away_points}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditResult(result)}><Edit size={14} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteResult(result.id)}><Trash2 size={14} className="text-destructive" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fixture Dates - Full view with filtering */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-semibold">Fixture Dates ({seasonFixtures.length})</h2>
                {Object.keys(fixtureEdits).length > 0 && (
                  <Button size="sm" onClick={handleSaveFixtureDates} disabled={savingFixtures} data-testid="save-fixture-dates-btn">
                    <Save size={14} className="mr-1" /> {savingFixtures ? "Saving..." : `Save (${Object.keys(fixtureEdits).length})`}
                  </Button>
                )}
              </div>

              {/* Club filter for fixtures */}
              {seasonFixtures.length > 0 && (
                <div className="mb-3">
                  <Select value={fixtureClubFilter} onValueChange={setFixtureClubFilter}>
                    <SelectTrigger className="w-full" data-testid="admin-fixture-club-filter">
                      <Users size={14} className="mr-2" />
                      <SelectValue placeholder="All Clubs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clubs</SelectItem>
                      {[...clubs].sort((a, b) => a.name.localeCompare(b.name)).map((club) => (
                        <SelectItem key={club.id} value={club.id}>{club.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {seasonFixtures.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No fixtures for this season. Generate fixtures first.</p>
              ) : (
                <div className="space-y-2">
                  {[...seasonFixtures]
                    .filter(f => fixtureClubFilter === 'all' || f.home_club_id === fixtureClubFilter || f.away_club_id === fixtureClubFilter)
                    .sort((a, b) => {
                      const nameA = `${a.home_club_name} vs ${a.away_club_name}`;
                      const nameB = `${b.home_club_name} vs ${b.away_club_name}`;
                      return nameA.localeCompare(nameB);
                    })
                    .map((fixture) => (
                    <div key={fixture.id} className={`bg-white rounded-lg border p-3 ${fixtureEdits[fixture.id] !== undefined ? 'border-primary' : fixture.has_conflict ? 'border-red-300' : 'border-border'}`}>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{fixture.home_club_name} vs {fixture.away_club_name}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {fixture.is_completed && !fixture.has_conflict && <span className="text-xs text-green-600 font-medium">Confirmed</span>}
                            {fixture.has_conflict && <span className="text-xs text-red-600 font-medium">Conflict</span>}
                            {!fixture.is_completed && !fixture.has_conflict && pendingResults.filter(p => p.fixture_id === fixture.id).length > 0 && (
                              <span className="text-xs text-amber-600 font-medium">Pending</span>
                            )}
                            {pendingResults.filter(p => p.fixture_id === fixture.id).map((pe, pi) => {
                              const clubName = clubs.find(c => c.id === pe.entered_by_club_id)?.name || 'Unknown';
                              return (
                                <span key={pi} className="text-xs text-muted-foreground">
                                  {clubName}: {pe.home_score}-{pe.away_score}
                                  <button 
                                    onClick={() => handleEditPending(pe)} 
                                    className="ml-1 text-primary hover:underline"
                                    data-testid={`edit-pending-${pe.id}`}
                                  >
                                    edit
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <Input type="date" value={fixtureEdits[fixture.id] ?? fixture.match_date ?? ""} onChange={(e) => handleFixtureDateChange(fixture.id, e.target.value)} className="w-40 text-sm" disabled={fixture.is_completed} />
                        {(fixture.is_completed || fixture.has_conflict || pendingResults.some(p => p.fixture_id === fixture.id)) && (
                          <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 shrink-0" onClick={() => handleResetFixture(fixture)} data-testid={`reset-fixture-${fixture.id}`} title="Reset fixture">
                            <RotateCcw size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4" data-testid="settings-tab-content">
            {/* Header Image Upload - Super Admin Only */}
            {isSuperAdmin && (
            <div className="bg-white rounded-lg border border-border p-4">
              <Label className="flex items-center gap-2 mb-3">
                <Image size={16} /> Homepage Header Image
              </Label>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
                <p className="text-xs text-blue-800">
                  <strong>Recommended:</strong> 1200 x 400px (3:1 ratio). Max 2MB.
                </p>
              </div>
              
              <input
                type="file"
                ref={headerImageRef}
                onChange={handleHeaderImageUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              
              {adminSettings?.header_image_path ? (
                <div className="relative rounded-lg overflow-hidden mb-3">
                  <img 
                    src={`${API}/images/${adminSettings.header_image_path.replace('ketley-league/', '')}?v=${imgCacheKey}`}
                    alt="Header"
                    className="w-full h-24 object-cover"
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute bottom-2 right-2"
                    onClick={() => headerImageRef.current?.click()}
                    disabled={uploadingHeader}
                  >
                    {uploadingHeader ? "Uploading..." : "Change Image"}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-20 border-dashed"
                  onClick={() => headerImageRef.current?.click()}
                  disabled={uploadingHeader}
                >
                  {uploadingHeader ? (
                    <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Upload size={20} className="mr-2" />
                      Upload Header Image
                    </>
                  )}
                </Button>
              )}
            </div>
            )}

            {/* Background Images - Super Admin Only */}
            {isSuperAdmin && (
            <div className="bg-white rounded-lg border border-border p-4">
              <Label className="flex items-center gap-2 mb-1">
                <Image size={16} /> Background Images
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Up to 12 images. One is randomly shown per visitor session, replacing the green pattern on wider screens.
              </p>
              
              <input
                type="file"
                ref={bgImageRef}
                onChange={handleBgImageUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />

              {/* Image grid */}
              {adminSettings?.background_images?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {adminSettings.background_images.map((path, i) => (
                    <div key={i} className="relative rounded-lg overflow-hidden border border-border">
                      <img 
                        src={`${API}/images/${path.replace('ketley-league/', '')}?v=${imgCacheKey}`}
                        alt={`Background ${i + 1}`}
                        className="w-full h-20 object-cover"
                      />
                      <button
                        onClick={() => handleDeleteBgImage(path)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-sm"
                        data-testid={`delete-bg-${i}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {(adminSettings?.background_images?.length || 0) < 12 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-dashed"
                  onClick={() => bgImageRef.current?.click()}
                  disabled={uploadingBg}
                  data-testid="upload-bg-btn"
                >
                  {uploadingBg ? "Uploading..." : `Add Background (${adminSettings?.background_images?.length || 0}/12)`}
                </Button>
              )}
            </div>
            )}
            
            {/* League Settings - Super Admin Only */}
            {isSuperAdmin && (
            <div className="bg-white rounded-lg border border-border p-4 space-y-4">
              <div>
                <Label>League Name</Label>
                <Input value={settingsForm.league_name} onChange={(e) => setSettingsForm({ ...settingsForm, league_name: e.target.value })} placeholder="The Ketley League" data-testid="league-name-input" />
              </div>
              <div>
                <Label>Super Admin PIN</Label>
                <Input value={settingsForm.super_admin_pin} onChange={(e) => setSettingsForm({ ...settingsForm, super_admin_pin: e.target.value.toUpperCase() })} placeholder="SUP1234" maxLength={7} data-testid="super-admin-pin-input" />
                <p className="text-xs text-muted-foreground mt-1">Full access to all features</p>
              </div>
              <div>
                <Label>League Rules</Label>
                <Textarea value={settingsForm.rules} onChange={(e) => setSettingsForm({ ...settingsForm, rules: e.target.value })} placeholder="Enter league rules..." rows={6} data-testid="rules-textarea" />
              </div>
              <Button onClick={handleSaveSettings} disabled={loading} className="w-full" data-testid="save-settings-btn">
                {loading ? "Saving..." : "Save League Settings"}
              </Button>
            </div>
            )}

            {/* Admin PIN & Contacts - Both Admin Types */}
            <div className="bg-white rounded-lg border border-border p-4 space-y-4">
              <div>
                <Label>Standard Admin PIN</Label>
                <Input value={settingsForm.standard_admin_pin} onChange={(e) => setSettingsForm({ ...settingsForm, standard_admin_pin: e.target.value.toUpperCase() })} placeholder="STD1234" maxLength={7} data-testid="standard-admin-pin-input" />
                <p className="text-xs text-muted-foreground mt-1">Access to clubs, fixtures, results, and contacts</p>
              </div>
              <div>
                <Label>Contact Information</Label>
                <Textarea value={settingsForm.contacts_info} onChange={(e) => setSettingsForm({ ...settingsForm, contacts_info: e.target.value })} placeholder="Enter contact details..." rows={4} data-testid="contacts-textarea" />
              </div>
              <Button onClick={handleSaveSettings} disabled={loading} className="w-full" data-testid="save-admin-settings-btn">
                {loading ? "Saving..." : "Save Admin Settings"}
              </Button>
            </div>
          </TabsContent>

          {/* Logs Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="logs" className="space-y-4" data-testid="logs-tab-content">
              <h2 className="font-semibold">Admin Activity Log</h2>
              {adminLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No logs yet</p>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {adminLogs.map((log) => (
                      <div key={log.id} className="bg-white rounded-lg border border-border p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            log.action.includes('conflict') ? 'bg-red-100 text-red-700' :
                            log.action.includes('entry') ? 'bg-blue-100 text-blue-700' :
                            log.action.includes('confirmed') ? 'bg-green-100 text-green-700' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {log.action.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock size={12} /> {formatLogTime(log.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm">{log.details}</p>
                        {log.club_name && <p className="text-xs text-muted-foreground mt-1">By: {log.club_name}</p>}
                        {log.admin_type && <p className="text-xs text-muted-foreground">Admin: {log.admin_type.replace('_', ' ')}</p>}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Club Dialog */}
      <Dialog open={showClubDialog} onOpenChange={setShowClubDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingClub ? "Edit Club" : "Add Club"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Club Name *</Label>
              <Input value={clubForm.name} onChange={(e) => setClubForm({ ...clubForm, name: e.target.value })} placeholder="Club name" data-testid="club-name-input" />
            </div>
            <div>
              <Label>Club PIN * (3 letters + 4 numbers)</Label>
              <Input value={clubForm.pin} onChange={(e) => setClubForm({ ...clubForm, pin: e.target.value.toUpperCase() })} placeholder="e.g., OAK1001" maxLength={7} data-testid="club-pin-input" />
            </div>

            {/* Course Data */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                <Trophy size={14} /> Course Data
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Slope Rating</Label>
                  <Input type="text" inputMode="decimal" value={clubForm.slope_rating} onChange={(e) => setClubForm({ ...clubForm, slope_rating: e.target.value })} placeholder="113" data-testid="slope-rating-input" />
                </div>
                <div>
                  <Label>Course Rating</Label>
                  <Input type="text" inputMode="decimal" value={clubForm.course_rating} onChange={(e) => setClubForm({ ...clubForm, course_rating: e.target.value })} placeholder="72" data-testid="course-rating-input" />
                </div>
                <div>
                  <Label>Par</Label>
                  <Input type="text" inputMode="numeric" value={clubForm.par} onChange={(e) => setClubForm({ ...clubForm, par: e.target.value })} placeholder="72" data-testid="par-input" />
                </div>
              </div>
            </div>

            {/* Team Captain */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                <Trophy size={14} /> Team Captain
              </p>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input value={clubForm.captain_name} onChange={(e) => setClubForm({ ...clubForm, captain_name: e.target.value })} placeholder="Captain name" data-testid="captain-name-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={clubForm.captain_email} onChange={(e) => setClubForm({ ...clubForm, captain_email: e.target.value })} placeholder="captain@example.com" type="email" data-testid="captain-email-input" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={clubForm.captain_phone} onChange={(e) => setClubForm({ ...clubForm, captain_phone: e.target.value })} placeholder="Phone number" data-testid="captain-phone-input" />
                </div>
              </div>
            </div>

            {/* Club Administrator */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                <Shield size={14} /> Club Administrator
              </p>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input value={clubForm.admin_name} onChange={(e) => setClubForm({ ...clubForm, admin_name: e.target.value })} placeholder="Administrator name" data-testid="club-admin-name-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={clubForm.admin_email} onChange={(e) => setClubForm({ ...clubForm, admin_email: e.target.value })} placeholder="admin@example.com" type="email" data-testid="club-admin-email-input" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={clubForm.admin_phone} onChange={(e) => setClubForm({ ...clubForm, admin_phone: e.target.value })} placeholder="Phone number" data-testid="club-admin-phone-input" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClubDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveClub} disabled={loading || !clubForm.name || !clubForm.pin}>{loading ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Season Dialog */}
      <Dialog open={showSeasonDialog} onOpenChange={setShowSeasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSeason ? "Edit Season" : "Add Season"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Season Name *</Label>
              <Input value={seasonForm.name} onChange={(e) => setSeasonForm({ ...seasonForm, name: e.target.value })} placeholder="e.g., 2025 Season" data-testid="season-name-input" />
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input value={seasonForm.start_date} onChange={(e) => setSeasonForm({ ...seasonForm, start_date: e.target.value })} type="date" data-testid="season-start-input" />
            </div>
            <div>
              <Label>End Date *</Label>
              <Input value={seasonForm.end_date} onChange={(e) => setSeasonForm({ ...seasonForm, end_date: e.target.value })} type="date" data-testid="season-end-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={seasonForm.is_active} onCheckedChange={(checked) => setSeasonForm({ ...seasonForm, is_active: checked })} data-testid="season-active-switch" />
              <Label>Set as active season</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSeasonDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSeason} disabled={loading || !seasonForm.name || !seasonForm.start_date || !seasonForm.end_date}>{loading ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Edit Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingResult?.status === 'conflict' ? 'Resolve Conflict' : 'Edit Result'}</DialogTitle>
            <DialogDescription>{editingResult && `${editingResult.home_club_name} vs ${editingResult.away_club_name}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {editingResult?.status === 'conflict' && conflictEntries.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-red-600 flex items-center gap-2">
                  <AlertTriangle size={14} /> Entries Received
                </p>
                {conflictEntries.map((entry, i) => {
                  const isHome = entry.entered_by_club_id === editingResult.home_club_id;
                  return (
                    <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid={`conflict-entry-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-red-800">{entry.entered_by_club_name}</span>
                        <span className="text-xs text-red-500">{isHome ? 'Home team' : 'Away team'}</span>
                      </div>
                      <p className="text-sm text-red-700">
                        Entered: <span className="font-bold">{entry.home_score} - {entry.away_score}</span>
                      </p>
                      {entry.entered_at && (
                        <p className="text-xs text-red-400 mt-1">
                          {(() => { try { return format(parseISO(entry.entered_at), "d MMM yyyy, HH:mm"); } catch { return entry.entered_at; } })()}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {editingResult?.status === 'conflict' && conflictEntries.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">Teams entered different scores. Enter the correct result below.</p>
              </div>
            )}
            <div>
              <p className="text-sm font-semibold mb-2">Set Official Result</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{editingResult?.home_club_name || 'Home'} Score</Label>
                  <Input type="number" min="0" step="0.5" value={resultForm.home_score} onChange={(e) => setResultForm({ ...resultForm, home_score: e.target.value })} className="text-center text-xl" data-testid="edit-home-score" />
                </div>
                <div>
                  <Label>{editingResult?.away_club_name || 'Away'} Score</Label>
                  <Input type="number" min="0" step="0.5" value={resultForm.away_score} onChange={(e) => setResultForm({ ...resultForm, away_score: e.target.value })} className="text-center text-xl" data-testid="edit-away-score" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">Points will be recalculated automatically</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveResult} disabled={loading}>{loading ? "Saving..." : editingResult?.status === 'conflict' ? 'Resolve Conflict' : 'Save Result'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Pending Entry Dialog */}
      <Dialog open={!!editingPending} onOpenChange={(open) => { if (!open) setEditingPending(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Pending Entry</DialogTitle>
            <DialogDescription>
              {editingPending && (() => {
                const fixture = fixtures.find(f => f.id === editingPending.fixture_id);
                const clubName = clubs.find(c => c.id === editingPending.entered_by_club_id)?.name || 'Unknown';
                return `${fixture?.home_club_name || '?'} vs ${fixture?.away_club_name || '?'} — entered by ${clubName}`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Home Score</Label>
              <Input type="text" inputMode="decimal" value={pendingForm.home_score} onChange={(e) => setPendingForm({...pendingForm, home_score: e.target.value})} data-testid="pending-home-score" />
            </div>
            <div>
              <Label>Away Score</Label>
              <Input type="text" inputMode="decimal" value={pendingForm.away_score} onChange={(e) => setPendingForm({...pendingForm, away_score: e.target.value})} data-testid="pending-away-score" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPending(null)}>Cancel</Button>
            <Button onClick={handleSavePending} disabled={loading} data-testid="save-pending-btn">{loading ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ADMIN_SESSION_KEY = 'ketley_admin_session';
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

const getStoredSession = () => {
  try {
    const raw = sessionStorage.getItem(ADMIN_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (Date.now() - session.timestamp > SESSION_DURATION_MS) {
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    return null;
  }
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  // Restore session from sessionStorage on mount
  const storedSession = getStoredSession();
  const [isAuthenticated, setIsAuthenticated] = useState(!!storedSession);
  const [adminPin, setAdminPin] = useState(storedSession?.pin || "");
  const [adminType, setAdminType] = useState(storedSession?.adminType || "");

  const handleVerifyPin = async (pinValue) => {
    const submitPin = pinValue || pin;
    if (submitPin.length !== 7) return;
    setLoading(true);
    try {
      const response = await apiClient.post('/admin/verify-pin', { pin: submitPin });
      const type = response.data.admin_type;
      setAdminPin(submitPin);
      setAdminType(type);
      setIsAuthenticated(true);
      sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({ pin: submitPin, adminType: type, timestamp: Date.now() }));
      toast.success(`${type === 'super_admin' ? 'Super Admin' : 'Standard Admin'} access granted for 30 minutes`);
    } catch (error) {
      toast.error("Invalid admin PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setIsAuthenticated(false);
    setPin("");
    setAdminPin("");
    setAdminType("");
    navigate("/admin");
  }, [navigate]);

  if (isAuthenticated) {
    return <AdminDashboard adminPin={adminPin} adminType={adminType} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen" data-testid="admin-page" style={{ backgroundColor: '#ffffff', position: 'relative', zIndex: 10 }}>
      <div className="bg-primary px-4 pt-8 pb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-white hover:bg-white/20" data-testid="admin-back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-extrabold text-white tracking-tight">Admin Access</h1>
            <p className="text-white/70 text-sm mt-1">Enter admin PIN to continue</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 pb-28">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Lock size={36} className="text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Enter Admin PIN</h2>
          <p className="text-sm text-muted-foreground mt-1">Format: 3 letters + 4 numbers (e.g., ABC1234)</p>
        </div>
        <PinInput value={pin} onChange={setPin} onSubmit={handleVerifyPin} loading={loading} />
      </div>
    </div>
  );
}
